import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import React from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import LoginPage from "../auth/LoginPage";
import { useAuth } from "../context/AuthContext";
import "./AccessGate.css";

const ACCESS_CONFIG = {
  forum: {
    flag: "can_access_forum",
    title: "Accès Forum Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder au forum pour le moment.",
  },
  chat: {
    flag: "can_access_chat",
    title: "Accès Chat Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder au chat pour le moment.",
  },
  video: {
    flag: "can_access_video",
    title: "Accès Vidéo Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder aux salons vidéo pour le moment.",
  },
  animaux: {
    flag: "can_access_animaux",
    title: "Accès Animaux Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder à la rubrique Animaux pour le moment.",
  },
  musique: {
    flag: "can_access_musique",
    title: "Accès Musique Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder à la rubrique Musique & clips vidéo pour le moment.",
  },
  market: {
    flag: "can_access_market",
    title: "Accès Market Bloqué",
    detail:
      "Ton compte n’a pas l’autorisation d’accéder à cette rubrique du Market pour le moment.",
  },
  support: {
    flag: null,
    allowPendingLegal: true,
    title: "Support Eternua",
    detail: "Le support reste accessible à tout compte authentifié.",
  },
  wallet: {
    flag: null,
    title: "Accès Portefeuille Bloqué",
    detail: "Ton compte doit être connecté pour accéder à son portefeuille privé.",
  },
  infini: {
    flag: null,
    title: "Accès Mon Infini bloqué",
    detail: "Mon Infini est un espace authentifié dont les capacités sont vérifiées séparément.",
  },
};

export default function AccessGate({
  type,
  allowAnonymous = false,
  redirectUnauthenticated = "",
  children,
}) {
  useSourceLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const config = ACCESS_CONFIG[type] || ACCESS_CONFIG.forum;
  const accessFlag = config.flag && user?.access
    ? user.access[config.flag]
    : true;
  const emailVerificationRequired = Boolean(user) && (
    user?.access?.email_verified === false
    || user?.access?.account_status === "pending_email_verification"
  );
  const currentDestination = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
  const legalAcceptanceRequired = Boolean(user) && (
    user?.access?.terms_current !== true
    || user?.access?.charter_current !== true
    || user?.access?.privacy_current !== true
  );
  const emailVerificationDestination = `/verify-email?${new URLSearchParams({
    next: currentDestination,
  }).toString()}`;

  if (loading) {
    return (
      <div className="access-gate">
        <div className="access-card">
          <div className="access-title"><LocalizedText source={"Vérification des accès…"} /></div>
          <div className="access-subtitle"><LocalizedText source={"Synchronisation avec la matrice Eternua."} /></div>
        </div>
      </div>
    );
  }

  if (!user) {
    if (allowAnonymous) return children;
    if (redirectUnauthenticated) {
      return <Navigate replace to={`/login?next=${encodeURIComponent(redirectUnauthenticated)}`} />;
    }
    return <LoginPage embedded />;
  }

  if (legalAcceptanceRequired && !config.allowPendingLegal) {
    return (
      <Navigate
        replace
        to={`/acceptation-regles?next=${encodeURIComponent(currentDestination)}`}
      />
    );
  }

  if (accessFlag === false) {
    return (
      <div className="access-gate">
        <div className="access-card access-card--blocked">
          <div className="access-badge"><LocalizedText source={"Accès refusé"} /></div>
          <div className="access-title">{<LocalizedText source={config.title} />}</div>
          <div className="access-subtitle">
            {emailVerificationRequired
              ? <LocalizedText source={"Ton adresse e-mail n’est pas encore confirmée. Cette étape peut bloquer certaines fonctions de cet espace."} />
              : <LocalizedText source={config.detail} />}
          </div>
          <div className="access-meta">
            {emailVerificationRequired
              ? <LocalizedText source={"Confirme-la d’abord. Si l’accès reste bloqué ensuite, contacte l’administrateur."} />
              : <LocalizedText source={"Contacte l’administrateur pour réactiver ton accès."} />}
          </div>
          <div className="access-actions">
            {emailVerificationRequired ? (
              <Link
                className="access-btn access-btn--primary"
                to={emailVerificationDestination}
              ><LocalizedText source={"Confirmer mon e-mail"} /></Link>
            ) : null}
            <button
              className="access-btn access-btn--ghost"
              onClick={() => navigate("/profil")}
            ><LocalizedText source={"Voir mon profil"} /></button>
            <button
              className="access-btn access-btn--secondary"
              onClick={() => navigate("/")}
            ><LocalizedText source={"Retour à l’accueil"} /></button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
